import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { msalInstance, loginRequest, tokenRequest } from "../config/authConfig";

// Define types for authentication state
interface AuthState {
  isAuthenticated: boolean;
  isAuthChecking: boolean;
  userRoles: string[];
  userName: string | null;
}

// Initial authentication state
const initialState: AuthState = {
  isAuthenticated: false,
  isAuthChecking: true,
  userRoles: [],
  userName: null,
};

// Define return type for login & checkAuthentication
interface AuthPayload {
  isAuthenticated: boolean;
  userName: string | undefined;
  userRoles: string[];
}

// Define an error type
interface AuthError {
  errorMessage: string;
}

// **Thunk: Silent Authentication Check on App Load**
export const checkAuthentication = createAsyncThunk<
  AuthPayload,
  void,
  { rejectValue: AuthError }
>("auth/checkAuthentication", async (_, { rejectWithValue }) => {
  try {
    //  Check if logout was intentional
    if (localStorage.getItem("logoutIntent") === "true") {
      console.warn(" Skipping auto-login because logout was intentional.");
      return rejectWithValue({ errorMessage: "User intentionally logged out" });
    }

    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      try {
        await msalInstance.ssoSilent(loginRequest);
      } catch {
        // ssoSilent fails when there is no SSO hint; fall back to loginPopup
        // which auto-completes in corporate AAD environments without user input
        try {
          await msalInstance.loginPopup(loginRequest);
        } catch {
          return rejectWithValue({
            errorMessage: "Login required. User interaction needed.",
          });
        }
      }
    }

    // Fetch the latest accounts **AFTER** attempting silent login
    const activeAccounts = msalInstance.getAllAccounts();
    if (activeAccounts.length === 0) {
      return rejectWithValue({
        errorMessage: "Login failed. No account found after login attempt.",
      });
    }

    msalInstance.setActiveAccount(activeAccounts[0]);

    // Fetch API token
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: activeAccounts[0],
    });
    const tokenPayload = JSON.parse(atob(response.accessToken.split(".")[1]));
    const userRoles: string[] = tokenPayload.roles || [];

    // console.log(" Silent Login Successful. User Roles:", userRoles);
    return {
      isAuthenticated: true,
      userName: activeAccounts[0].name,
      userRoles,
    };
  } catch (error) {
    return rejectWithValue({
      errorMessage: "Silent login failed: " + (error as Error).message,
    });
  }
});

// **Thunk: Manual Login**
export const login = createAsyncThunk<
  AuthPayload,
  void,
  { rejectValue: AuthError }
>("auth/login", async (_, { rejectWithValue }) => {
  try {
    await msalInstance.loginPopup(loginRequest);
    const accounts = msalInstance.getAllAccounts();
    msalInstance.setActiveAccount(accounts[0]);

    // Fetch API token after login
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: accounts[0],
    });

    // Clear logout flag because user logged in manually
    if (localStorage.getItem("logoutIntent") === "true") {
      localStorage.removeItem("logoutIntent");
    }

    const tokenPayload = JSON.parse(
      atob(tokenResponse.accessToken.split(".")[1]),
    );
    const userRoles: string[] = tokenPayload.roles || [];

    // console.log("Login Successful. User Roles:", userRoles);
    return { isAuthenticated: true, userName: accounts[0].name, userRoles };
  } catch (error) {
    return rejectWithValue({
      errorMessage: "Login failed: " + (error as Error).message,
    });
  }
});

// **Thunk: Logout Function**
export const logout = createAsyncThunk<void, void, { rejectValue: AuthError }>(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      localStorage.setItem("logoutIntent", "true"); // Set logout flag
      await msalInstance.logoutPopup();
    } catch (error) {
      return rejectWithValue({
        errorMessage: "Logout failed: " + (error as Error).message,
      });
    }
  },
);

// **Auth Slice**
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Silent Login (on App Load)
      .addCase(checkAuthentication.pending, (state) => {
        state.isAuthChecking = true;
      })
      .addCase(
        checkAuthentication.fulfilled,
        (state, action: PayloadAction<AuthPayload>) => {
          // console.log("Silent Login - Redux State Updated:", action.payload);
          state.isAuthenticated = action.payload.isAuthenticated;
          state.userName = action.payload.userName || "Unknown User";
          state.userRoles = action.payload.userRoles;
          state.isAuthChecking = false;
        },
      )
      .addCase(checkAuthentication.rejected, (state) => {
        state.isAuthenticated = false;
        state.userRoles = [];
        state.userName = null;
        state.isAuthChecking = false;
      })
      // Manual Login
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthPayload>) => {
        // console.log("Login - Redux State Updated:", action.payload);
        state.isAuthenticated = action.payload.isAuthenticated;
        state.userName = action.payload.userName || "Unknown User";
        state.userRoles = action.payload.userRoles;
      })
      .addCase(login.rejected, (state, action) => {
        console.error("Login failed:", action.payload?.errorMessage);
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        console.log("Logout - Redux State Cleared");
        state.isAuthenticated = false;
        state.userRoles = [];
        state.userName = null;
      });
  },
});

export default authSlice.reducer;
